import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Target, Settings, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import beratungHero from "@/assets/beratung-hero.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const offerings = [
  {
    icon: Building2,
    title: "Organisationsentwicklung",
    href: "/portfolio/beratung/organisationsentwicklung",
    description:
      "Wir begleiten Organisationen bei Veränderungsprozessen, Strukturentwicklung und kulturellem Wandel - systemisch, partizipativ und nachhaltig.",
  },
  {
    icon: Target,
    title: "Strategieberatung",
    href: "/portfolio/beratung/strategieberatung",
    description:
      "Gemeinsam entwickeln wir tragfähige Strategien, die Ihre Ziele mit den Ressourcen und der Kultur Ihrer Organisation in Einklang bringen - und bündeln dies mit einer starken Erzählung. So ermöglichen Sie Ihren Mitarbeiter:innen, sich mit dem notwendigen Wandel zu identifizieren und Veränderungen wirksam zu gestalten.",
  },
  {
    icon: Settings,
    title: "Prozessberatung",
    href: "/portfolio/beratung/prozessberatung",
    description:
      "Veränderungen scheitern oft am Hang zu Status Quo, schwacher Kommunikation und fehlender Klarheit. Wir analysieren Ihre internen Prozessabläufe und Kommunikation. Wir schaffen Räume, um sich offen mit ihnen auseinanderzusetzen. Gemeinsam denken wir diese neu.",
  },
];

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Organisationsberatung",
  description: "Strategische Organisationsberatung, Organisationsentwicklung und Prozessberatung – systemisch, partizipativ und nachhaltig. Begleitung von Veränderungsprozessen in Organisationen.",
  provider: {
    "@type": "Organization",
    name: "Leadership Focus GmbH",
    url: "https://leadership-focus.de",
  },
  areaServed: [
    { "@type": "Country", name: "Deutschland" },
    { "@type": "Country", name: "Österreich" },
    { "@type": "Country", name: "Schweiz" },
  ],
  serviceType: "Organisationsberatung",
};

const Beratung = () => (
  <>
    <SEOHead title="Beratung – Leadership Focus" description="Strategische Organisationsberatung und Begleitung von Veränderungsprozessen. Leadership Focus in München." canonical="https://leadership-focus.de/portfolio/beratung" />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }} />
    <Header />
    <main className="py-20 md:py-28">
      <div className="container">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center mb-14"
        >
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            Beratung
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Strategische Beratung und Begleitung von Veränderungsprozessen – maßgeschneidert für Ihre Organisation.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mb-14 rounded-2xl overflow-hidden shadow-lg"
        >
          <img
            src={beratungHero}
            alt="Leadership Focus Beratungsteam"
            className="w-full h-64 md:h-96 object-cover object-[center_25%]"
          />
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {offerings.map((item) => (
            <motion.div key={item.title} variants={fadeInUp}>
              <Link to={item.href} className="block h-full">
                <Card className="h-full border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                  <CardContent className="p-8">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground mb-2 font-serif">{item.title}</h2>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                      Mehr erfahren
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </main>
    <Footer />
  </>
);

export default Beratung;
